import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  archiveCustomKpi,
  createCustomKpi,
  createCustomKpiVersion,
  getCustomKpiCatalog,
  getCustomKpis,
  getCustomKpiUsage,
  migrateCustomKpiTemplates,
  previewCustomKpiMigration,
  updateCustomKpi,
} from '@/services/api';
import type {
  CreateCustomKpiRequest,
  CustomKpiMigrationRequest,
  UpdateCustomKpiRequest,
} from '@/types/template';

export const CUSTOM_KPIS_KEY = 'customKpis';
export const CUSTOM_KPI_USAGE_KEY = 'customKpiUsage';

export function useCustomKpis(teamId: string | undefined, includeArchived: boolean = false) {
  return useQuery({
    queryKey: [CUSTOM_KPIS_KEY, teamId, includeArchived],
    queryFn: () => getCustomKpis(teamId!, includeArchived),
    enabled: !!teamId,
  });
}

export function useCustomKpiCatalog(teamId: string | undefined) {
  return useQuery({
    queryKey: [CUSTOM_KPIS_KEY, teamId, 'catalog'],
    queryFn: () => getCustomKpiCatalog(teamId!),
    enabled: !!teamId,
  });
}

export function useCreateCustomKpi(teamId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomKpiRequest) => createCustomKpi(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_KPIS_KEY, teamId] });
    },
  });
}

export function useUpdateCustomKpi(teamId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customKpiId, data }: { customKpiId: string; data: UpdateCustomKpiRequest }) =>
      updateCustomKpi(teamId!, customKpiId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_KPIS_KEY, teamId] });
    },
  });
}

export function useCreateCustomKpiVersion(teamId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customKpiId,
      data,
    }: {
      customKpiId: string;
      data: UpdateCustomKpiRequest;
    }) => createCustomKpiVersion(teamId!, customKpiId, data),
    onSuccess: (_definition, { customKpiId }) => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_KPIS_KEY, teamId] });
      queryClient.invalidateQueries({ queryKey: [CUSTOM_KPI_USAGE_KEY, teamId, customKpiId] });
    },
  });
}

export function useArchiveCustomKpi(teamId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customKpiId: string) => archiveCustomKpi(teamId!, customKpiId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_KPIS_KEY, teamId] });
    },
  });
}

export function useCustomKpiUsage(
  teamId: string | undefined,
  customKpiId: string | undefined
) {
  return useQuery({
    queryKey: [CUSTOM_KPI_USAGE_KEY, teamId, customKpiId],
    queryFn: () => getCustomKpiUsage(teamId!, customKpiId!),
    enabled: !!teamId && !!customKpiId,
  });
}

export function usePreviewCustomKpiMigration(teamId: string | undefined) {
  return useMutation({
    mutationFn: ({
      customKpiId,
      data,
    }: {
      customKpiId: string;
      data: CustomKpiMigrationRequest;
    }) => previewCustomKpiMigration(teamId!, customKpiId, data),
  });
}

export function useMigrateCustomKpiTemplates(teamId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customKpiId,
      data,
    }: {
      customKpiId: string;
      data: CustomKpiMigrationRequest;
    }) => migrateCustomKpiTemplates(teamId!, customKpiId, data),
    onSuccess: (_response, { customKpiId }) => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_KPIS_KEY, teamId] });
      queryClient.invalidateQueries({ queryKey: [CUSTOM_KPI_USAGE_KEY, teamId, customKpiId] });
    },
  });
}
